import { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';
import { generateUniqueAnonymizedUsername } from '../utils/anonymizer';

const UserContext = createContext({});

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const { currentUser, isAuthenticated, loading: authLoading } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated || !currentUser) {
      setUserData(null);
      setLoading(false);
      return;
    }

    // Reset loading while we fetch the user document
    setLoading(true);

    // Set up real-time listener for user document
    const userDocRef = doc(db, 'users', currentUser.uid);
    const unsubscribe = onSnapshot(
      userDocRef,
      async (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();

          // Migrate existing users who don't have an anonymized username
          if (!data.anonymizedUsername) {
            try {
              const anonymizedUsername = await generateUniqueAnonymizedUsername(db);
              await updateDoc(userDocRef, {
                anonymizedUsername,
                reputation: data.reputation || {
                  rating: 0,
                  totalRatings: 0,
                  completedBounties: 0,
                  postedBounties: 0,
                },
                isAdmin: data.isAdmin ?? false,
                updatedAt: serverTimestamp(),
              });
              // onSnapshot will fire again with updated data
            } catch (err) {
              console.error('Error migrating user:', err);
              setUserData({ id: docSnap.id, ...data });
              setLoading(false);
            }
          } else {
            // Reload Auth user to get latest email (e.g. after verification)
            try {
              await currentUser.reload();
              if (currentUser.email && data.email !== currentUser.email) {
                await updateDoc(userDocRef, { email: currentUser.email });
                // onSnapshot will fire again with updated doc
                return;
              }
            } catch (err) {
              console.error('Error syncing email:', err);
            }
            setUserData({ id: docSnap.id, ...data });
            setLoading(false);
          }
        } else {
          setUserData(null);
          setLoading(false);
        }
      },
      (error) => {
        console.error('Error fetching user data:', error);
        setUserData(null);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [currentUser, isAuthenticated, authLoading]);

  const value = {
    userData,
    loading,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
